import { useEffect, useRef, useState } from 'react';
import classes from './SiteScrollbar.module.scss';


// Вертикальная «HUD»-полоса прокрутки, как на geekstudio: жёлтая дорожка заполняется
// по ходу страницы, бегунок с процентом тянется мышью, клик по рельсу прокручивает,
// при быстрой прокрутке бегунок вытягивается, рельс слегка наклоняется за мышью.
// Только десктоп с мышью; системная полоса там скрывается. Анимация — один
// requestAnimationFrame, который засыпает, когда всё доехало: React не перерисовывается.
const QUERY = '(min-width: 1280px) and (hover: hover) and (pointer: fine)';
const RAIL_HEIGHT = 260;
const THUMB_HEIGHT = 44;
const MAX_TRAVEL = RAIL_HEIGHT - THUMB_HEIGHT;
const MARKS = [ 0, 25, 50, 75, 100 ];
const NEAR = 7;
const HIDE_DELAY = 2600;

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const maxScroll = () => document.documentElement.scrollHeight - window.innerHeight;

const SiteScrollbar = () => {
  const [ isEnabled, setIsEnabled ] = useState(() => window.matchMedia(QUERY).matches);

  useEffect(() => {
    const media = window.matchMedia(QUERY);
    const onChange = () => setIsEnabled(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return isEnabled ? <ScrollbarRail /> : null;
};

const ScrollbarRail = () => {
  const hudRef = useRef(null);
  const railRef = useRef(null);
  const trailRef = useRef(null);
  const thumbRef = useRef(null);
  const percentRef = useRef(null);
  const marksRef = useRef([]);

  useEffect(() => {
    const root = document.documentElement;
    const hud = hudRef.current;
    const rail = railRef.current;
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.classList.add('has-hud-scroll');

    const state = {
      progress: 0,
      shown: 0,
      lastY: window.scrollY,
      lastTime: performance.now(),
      stretch: 1,
      stretchTarget: 1,
      tilt: { x: 0, y: 0 },
      tiltTarget: { x: 0, y: 0 },
      percent: -1,
      marks: '',
    };
    let frame = 0;
    let hideTimer = 0;
    let isHovered = false;
    let isDragging = false;

    const readProgress = () => {
      const max = maxScroll();
      // Короткая страница без прокрутки — рельс не нужен.
      hud.dataset.hidden = max < 40 ? 'true' : 'false';
      state.progress = max > 0 ? clamp(window.scrollY / max) : 0;
    };

    const render = (now) => {
      const k = calm ? 1 : 0.2;
      state.shown += (state.progress - state.shown) * k;
      state.stretch += (state.stretchTarget - state.stretch) * 0.25;
      state.stretchTarget += (1 - state.stretchTarget) * 0.12;
      state.tilt.x += (state.tiltTarget.x - state.tilt.x) * 0.18;
      state.tilt.y += (state.tiltTarget.y - state.tilt.y) * 0.18;

      const { shown } = state;
      thumbRef.current.style.transform =
        `translate3d(0, ${shown * MAX_TRAVEL}px, 0) scaleY(${state.stretch.toFixed(3)})`;
      trailRef.current.style.transform = `scaleY(${shown.toFixed(4)})`;
      rail.style.transform =
        `rotateX(${state.tilt.x.toFixed(2)}deg) rotateY(${state.tilt.y.toFixed(2)}deg)`;

      const percent = Math.round(shown * 100);
      if (percent !== state.percent) {
        state.percent = percent;
        percentRef.current.textContent = `${String(percent).padStart(2, '0')}%`;

        // Пройденные метки светятся, ближайшая к бегунку — пульсирует.
        let near = -1;
        const marks = MARKS.map((mark, i) => {
          if (near === -1 && Math.abs(percent - mark) < NEAR) near = i;
          return percent >= mark ? 'passed' : '';
        });
        if (near !== -1) marks[ near ] = 'near';
        const key = marks.join();
        if (key !== state.marks) {
          state.marks = key;
          marks.forEach((value, i) => { marksRef.current[ i ].dataset.state = value; });
        }
      }

      const settled =
        Math.abs(state.progress - state.shown) < 0.0005 &&
        Math.abs(state.stretch - 1) < 0.002 &&
        Math.abs(state.tilt.x - state.tiltTarget.x) < 0.05 &&
        Math.abs(state.tilt.y - state.tiltTarget.y) < 0.05;
      frame = settled ? 0 : requestAnimationFrame(render);
      state.lastTime = now;
    };
    const wake = () => {
      if (!frame) frame = requestAnimationFrame(render);
    };

    const show = () => {
      hud.dataset.active = 'true';
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        if (!isHovered && !isDragging) hud.dataset.active = 'false';
      }, HIDE_DELAY);
    };

    const onScroll = () => {
      const now = performance.now();
      const dt = Math.max(16, now - state.lastTime);
      const velocity = Math.abs(window.scrollY - state.lastY) / dt * 1000;
      state.lastY = window.scrollY;
      if (!calm) state.stretchTarget = Math.max(state.stretchTarget, 1 + (clamp(velocity / 2600) * 0.3));
      readProgress();
      show();
      wake();
    };

    // Высота страницы меняется (подгрузка данных, картинки) — пересчитываем.
    const observer = new ResizeObserver(() => {
      readProgress();
      wake();
    });
    observer.observe(document.body);

    const onRailMove = (event) => {
      if (calm) return;
      const rect = rail.getBoundingClientRect();
      const dx = clamp((event.clientX - rect.left - (rect.width / 2)) / 100, -1, 1);
      const dy = clamp((event.clientY - rect.top - (rect.height / 2)) / 140, -1, 1);
      state.tiltTarget = { x: -dy * 18, y: dx * 19 };
      wake();
    };
    const onRailEnter = () => {
      isHovered = true;
      hud.dataset.hovered = 'true';
      show();
    };
    const onRailLeave = () => {
      isHovered = false;
      hud.dataset.hovered = 'false';
      state.tiltTarget = { x: 0, y: 0 };
      show();
      wake();
    };
    const onRailClick = (event) => {
      if (event.target.closest(`.${classes.thumb}`)) return;
      const rect = rail.getBoundingClientRect();
      const ratio = clamp((event.clientY - rect.top - (THUMB_HEIGHT / 2)) / MAX_TRAVEL);
      window.scrollTo({ top: ratio * maxScroll(), behavior: calm ? 'auto' : 'smooth' });
    };

    const onThumbDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
      isDragging = true;
      hud.dataset.dragging = 'true';
      const startY = event.clientY;
      const startProgress = state.progress;
      const max = maxScroll();

      const onMove = (moveEvent) => {
        const ratio = clamp(startProgress + ((moveEvent.clientY - startY) / MAX_TRAVEL));
        window.scrollTo({ top: ratio * max, behavior: 'auto' });
      };
      const onUp = () => {
        isDragging = false;
        hud.dataset.dragging = 'false';
        show();
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    };

    const thumb = thumbRef.current;
    window.addEventListener('scroll', onScroll, { passive: true });
    rail.addEventListener('pointermove', onRailMove);
    rail.addEventListener('pointerenter', onRailEnter);
    rail.addEventListener('pointerleave', onRailLeave);
    rail.addEventListener('click', onRailClick);
    thumb.addEventListener('pointerdown', onThumbDown);

    readProgress();
    show();
    wake();

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(hideTimer);
      observer.disconnect();
      root.classList.remove('has-hud-scroll');
      window.removeEventListener('scroll', onScroll);
      rail.removeEventListener('pointermove', onRailMove);
      rail.removeEventListener('pointerenter', onRailEnter);
      rail.removeEventListener('pointerleave', onRailLeave);
      rail.removeEventListener('click', onRailClick);
      thumb.removeEventListener('pointerdown', onThumbDown);
    };
  }, []);

  return (
    <aside className={classes.hud} ref={hudRef} aria-hidden data-active="true">
      <div className={classes.rail} ref={railRef} data-cursor="hover">
        <div className={classes.track}>
          <span className={classes.line} />
          <span className={classes.trail} ref={trailRef} />
          {MARKS.map((mark, i) => (
            <span
              className={classes.mark}
              key={mark}
              ref={(node) => { marksRef.current[ i ] = node; }}
              style={{ top: (THUMB_HEIGHT / 2) + ((mark / 100) * MAX_TRAVEL) }}
            />
          ))}
        </div>

        <div className={classes.thumb} ref={thumbRef}>
          <div className={classes.label}>
            <span className={classes.pulse} />
            <span className={classes.labelName}>GEEKS</span>
            <span className={classes.labelDivider} />
            <span className={classes.percent} ref={percentRef}>00%</span>
          </div>
          <div className={classes.knob}>
            <span className={classes.knobHalo} />
            <span className={classes.knobBody} />
          </div>
        </div>
      </div>
    </aside>
  );
};

export default SiteScrollbar;
